import type { SerializedXYWH } from '@blocksuite/global/gfx';
import type { GfxCompatibleProps } from '@blocksuite/std/gfx';
import { GfxCompatible } from '@blocksuite/std/gfx';
import type { Text } from '@blocksuite/store';
import {
  BlockModel,
  BlockSchemaExtension,
  defineBlockSchema,
} from '@blocksuite/store';

import type { DatabaseIdentityProps } from './database-identity.js';
import { DatabaseBlockTransformer } from './database-transformer.js';
import type {
  ColumnDataType,
  SerializedCells,
  ViewBasicDataType,
} from './types.js';

export type CanvasDatabasePresentationMode = 'summary' | 'live';

export type CanvasDatabasePresentationViewportRowAnchor = {
  offsetPx: number;
  offsetRatio: number;
  rowId: string;
};

export type CanvasDatabasePresentationViewportTarget = {
  rowAnchor?: CanvasDatabasePresentationViewportRowAnchor;
  scrollLeft: number;
  scrollTop: number;
};

export type CanvasDatabasePresentationViewport = {
  scrollLeft: number;
  scrollTop: number;
  target?: string;
  targets?: Record<string, CanvasDatabasePresentationViewportTarget>;
};

export type CanvasDatabasePresentationState =
  | {
      mode: 'summary';
      updatedAt?: number;
    }
  | {
      mode: 'live';
      viewId: string;
      viewport: CanvasDatabasePresentationViewport;
      updatedAt?: number;
    };

export type DatabaseBlockProps = {
  views: ViewBasicDataType[];
  activeViewId?: string;
  title: Text;
  cells: SerializedCells;
  columns: Array<ColumnDataType>;
  comments?: Record<string, boolean>;
  /** sl-9qm.21.1: true = UI prevents manual edits (system-managed database) */
  systemManaged?: boolean;
  /** sl-9qm.21.1: identifies the managing system, e.g. "published-view:kb-analytics" */
  managedBy?: string;
  /** sl-9qm.21.1: true = moderation allows row deletion even on system-managed DBs */
  allowDelete?: boolean;
  /** sl-web-9n8: true = column structure locked (no add/remove/rename columns), rows still editable */
  schemaLocked?: boolean;
  /** true = database host block is scoped to the current document and must not be moved cross-doc */
  docScoped?: boolean;
  /** true = system-managed database host block is visible in document UI */
  visible?: boolean;
  /** Last remembered canvas position for the doc-scoped File Index database */
  fileIndexLastXYWH?: SerializedXYWH;
  /** File Index default view migration version */
  fileIndexViewVersion?: number;
  /** Canvas-only presentation state for edgeless database nodes. */
  canvasPresentation?: CanvasDatabasePresentationState;
} & DatabaseIdentityProps &
  GfxCompatibleProps;

export class DatabaseBlockModel extends GfxCompatible<DatabaseBlockProps>(
  BlockModel
) {}

export const DatabaseBlockSchema = defineBlockSchema({
  flavour: 'affine:database',
  props: (internal): DatabaseBlockProps => ({
    databaseId: undefined,
    databaseRootDocId: undefined,
    databaseStorageVersion: undefined,
    databaseShardManifest: undefined,
    views: [],
    activeViewId: undefined,
    title: internal.Text(),
    cells: Object.create(null),
    columns: [],
    comments: undefined,
    systemManaged: undefined,
    managedBy: undefined,
    allowDelete: undefined,
    schemaLocked: undefined,
    docScoped: undefined,
    visible: undefined,
    fileIndexLastXYWH: undefined,
    fileIndexViewVersion: undefined,
    canvasPresentation: undefined,
    // GfxCompatible props
    xywh: '[0,0,420,300]',
    index: 'a0',
    lockedBySelf: false,
  }),
  metadata: {
    role: 'hub',
    version: 3,
    parent: ['affine:note', '@root'],
    children: ['affine:paragraph', 'affine:list'],
  },
  transformer: transformerConfigs =>
    new DatabaseBlockTransformer(transformerConfigs),
  toModel: () => new DatabaseBlockModel(),
});

export const DatabaseBlockSchemaExtension =
  BlockSchemaExtension(DatabaseBlockSchema);
